import { Box, List as MantineList, ListProps, ThemeIcon, useMantineTheme } from '@mantine/core';
import { IconCheck, IconCircle } from '@tabler/icons-react';
import { forwardRef, ReactNode } from 'react';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export type ListVariant = 'default' | 'gradient' | 'check' | 'numbered';

export interface ThemedListProps extends Omit<ListProps, 'icon'> {
  variant?: ListVariant;
  gradientVariant?: GradientVariant;
  items?: string[];
  customIcon?: ReactNode;
}

const List = forwardRef<HTMLUListElement, ThemedListProps>(
  ({ variant = 'default', gradientVariant = 'primary', items, customIcon, children, ...rest }, ref) => {
    const theme = useMantineTheme();
    const gradient = theme.other.gradients[gradientVariant];

    const getIcon = () => {
      if (customIcon) return customIcon;

      switch (variant) {
        case 'check':
          return (
            <ThemeIcon
              size={20}
              radius="xl"
              style={{
                background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
              }}
            >
              <IconCheck size={12} color="white" />
            </ThemeIcon>
          );
        case 'gradient':
          return (
            <Box
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
              }}
            />
          );
        default:
          return <IconCircle size={8} />;
      }
    };

    if (items) {
      return (
        <MantineList
          ref={ref}
          icon={variant !== 'numbered' ? getIcon() : undefined}
          type={variant === 'numbered' ? 'ordered' : 'unordered'}
          {...rest}
        >
          {items.map((item, index) => (
            <MantineList.Item key={index}>{item}</MantineList.Item>
          ))}
        </MantineList>
      );
    }

    return (
      <MantineList
        ref={ref}
        icon={variant !== 'numbered' ? getIcon() : undefined}
        type={variant === 'numbered' ? 'ordered' : 'unordered'}
        {...rest}
      >
        {children}
      </MantineList>
    );
  }
);

List.displayName = 'List';

export default List;
